import { pipe } from './functional-helpers';
import config from '../configuration';
import { ErrorAlreadySent } from '../errors';

interface ErrorNonCompliantAttrs extends Error {
  // these 4 fields will only be available on some browsers
  fileName?: string;
  lineNumber?: number;
  columnNumber?: number;
  stack?: string;
}

export type ErrorObject = {
  partner_identifier: string | null;
  page_url: string;
  source: string;
  error: {
    // Error Info
    name: string;
    message: string;
    fileName?: string;
    lineNumber?: number;
    columnNumber?: number;
    stack?: string;
    // Additional Info
    userAgent: string;
    requestAnimationFrameSupport: boolean;
  };
};

export const SENT_MESSAGES: string[] = [];

const DENIED_MESSAGE_LIST: string[] = [
  'Failed to fetch',
  'cancelled',
  'NetworkError when attempting to fetch resource.',
  'The network connection was lost.',
  'The request timed out.',
  'cancelado',
  'The operation was aborted.',
  'Load failed',
  'The script will be loaded from another env'
];

export default (error: any, source = ''): Promise<any | null> => {
  if (isIgnoredErrorMessage(DENIED_MESSAGE_LIST, <ErrorNonCompliantAttrs>error)) return Promise.resolve(null);
  try {
    return pipe<[any, string | null], Promise<any>>(
      createErrorMessageObject,
      suppressDuplicateLogs,
      (obj: object): string => JSON.stringify(obj, (_, v: unknown): unknown => (v === undefined ? null : v)),
      sendErrorToAPIServer,
      handleResponsePromise
    )([error, source]);
  } catch (err) {
    if (err instanceof ErrorAlreadySent) {
      // refuse to send additional logs for this message
      // don't log, we don't want to consume any more ram & resources than we already are
      // console.warn('refusing to send additional logs for message', err);
      return Promise.resolve(null);
    }
    // unknown error, rethrow
    // todo handle sending this to error logs
    throw err;
  }
};

const isIgnoredErrorMessage = (ignoreErrorList: string[], error: ErrorNonCompliantAttrs): boolean => {
  if (!!error && !!ignoreErrorList) {
    return ignoreErrorList.some((errorMessage) => errorMessage.toLowerCase() === error.message.toLowerCase());
  }
  return false;
};

export function suppressDuplicateLogs(errorObject: ErrorObject): ErrorObject {
  // safety check - is errorObject and errorObject.error defined as an object?
  // If not, return as is, we don't know what to do with this object
  if (errorObject?.error == null || typeof errorObject.error !== 'object') return errorObject;
  // ensure error.message is defined
  if (!errorObject?.error?.message) errorObject.error.message = 'Error received without a message.';
  // check if error message has already been sent to logs
  if (SENT_MESSAGES.includes(errorObject.error.message))
    errorObject.error.message = `Message already sent: ${errorObject.error.message}`;
  // check if "Message already sent: {error}" has already been sent to logs - if so, stop execution
  if (SENT_MESSAGES.includes(errorObject.error.message)) throw new ErrorAlreadySent(errorObject.error.message);
  // finally, push the error message that will be sent to our tracking
  SENT_MESSAGES.push(errorObject.error.message);
  return errorObject;
}

const createErrorMessageObject = ([error, source]: [ErrorNonCompliantAttrs, string]): ErrorObject => {
  return {
    partner_identifier: config.partner?.partner_identifier || null,
    page_url: window.location.href,
    source: source,
    error: {
      // Error Info
      name: error?.name,
      message: error?.message,
      fileName: error?.fileName,
      lineNumber: error?.lineNumber,
      columnNumber: error?.columnNumber,
      stack: error?.stack,
      // Additional Info
      userAgent: window?.navigator?.userAgent,
      requestAnimationFrameSupport: !!window.requestAnimationFrame
    }
  };
};
function sendErrorToAPIServer(fetchBody: string): Promise<Response> {
  return fetch(config.urls.logException, {
    method: 'POST',
    credentials: 'omit',
    headers: {
      'Content-Type': 'application/json'
    },
    body: fetchBody
  });
}

const handleResponsePromise = (promise: Promise<Response>): Promise<any> => {
  return promise
    .then((response) => response.json())
    .then((data: object) => {
      console.log('Error sent to the API server:', data);
      return data;
    })
    .catch((error) => {
      console.error('An exception has occurred when sending error messages to the API server:\n', error);
      throw error;
    });
};
